/**
 * Consolidated Status Definitions
 * This file contains all status enums and objects for users, documents, projects, and orders
 * Used throughout the application for consistent status management
 */

// ===== USER STATUSES =====

export enum USER_ROLE_TYPE {
  CUSTOMER = 1,
  PROJECT_MANAGER = 3,
  ADMIN = 4,
  TRANSLATOR = 6,
  UNFINISHED = 7,
}

export const USER_ROLES = {
  [USER_ROLE_TYPE.CUSTOMER]: "Customer",
  [USER_ROLE_TYPE.PROJECT_MANAGER]: "Project Manager",
  [USER_ROLE_TYPE.ADMIN]: "Admin",
  [USER_ROLE_TYPE.TRANSLATOR]: "Translator",
  [USER_ROLE_TYPE.UNFINISHED]: "Unfinished",
} as const;

export type UserRoleType = keyof typeof USER_ROLES;

// ===== DOCUMENT STATUSES =====

export enum DOCUMENT_STATUS {
  NEW = 1,
  IN_PROGRESS = 2,
  FOR_REVIEW = 3,
  FOR_APPROVAL = 4,
  COMPLETED = 5,
}

export const DOCUMENT_STATUS_NAMES = {
  [DOCUMENT_STATUS.NEW]: "New",
  [DOCUMENT_STATUS.IN_PROGRESS]: "In Progress",
  [DOCUMENT_STATUS.FOR_REVIEW]: "For Review",
  [DOCUMENT_STATUS.FOR_APPROVAL]: "For Approval (Client)",
  [DOCUMENT_STATUS.COMPLETED]: "Completed",
} as const;

// Document status for translator view (simplified)
export const TRANSLATOR_DOCUMENT_STATUS = {
  [DOCUMENT_STATUS.NEW]: "New",
  [DOCUMENT_STATUS.IN_PROGRESS]: "In Progress",
  [DOCUMENT_STATUS.FOR_REVIEW]: "Completed",
  [DOCUMENT_STATUS.FOR_APPROVAL]: "Completed",
  [DOCUMENT_STATUS.COMPLETED]: "Completed",
} as const;

export type DocumentStatusType = keyof typeof DOCUMENT_STATUS_NAMES;

// ===== PROJECT STATUSES =====

export enum PROJECT_STATUS {
  DRAFT = 10,
  PAID = 1,
  PAID_ALT = 2, // Alternative paid status
  ASSIGNED = 3,
  IN_PROGRESS = 4,
  PARTIALLY_COMPLETED = 5,
  COMPLETED = 6,
  PENDING_REVIEW = 7,
}

export type ProjectStatusKey = 
  | "draft"
  | "paid"
  | "assigned"
  | "inProgress"
  | "partiallyCompleted"
  | "completed"
  | "pendingReview"
  | "active";

export interface ProjectStatusConfig {
  label: string;
  color: string;
  bgColor: string;
}

export const PROJECT_STATUS_CONFIG: Record<ProjectStatusKey, ProjectStatusConfig> = {
  draft: { label: "Draft", color: "#6B7280", bgColor: "#F3F4F6" },
  paid: { label: "Paid", color: "#FFFFFF", bgColor: "#3B82F6" },
  assigned: { label: "Assigned", color: "#FFFFFF", bgColor: "#8B5CF6" },
  inProgress: { label: "In Progress", color: "#FFFFFF", bgColor: "#F59E0B" },
  partiallyCompleted: { label: "Partially Completed", color: "#FFFFFF", bgColor: "#10B981" },
  completed: { label: "Completed", color: "#FFFFFF", bgColor: "#9AA7B8" },
  pendingReview: { label: "Pending Review", color: "#FFFFFF", bgColor: "#EF4444" },
  active: { label: "Active", color: "#FFFFFF", bgColor: "#22C55E" },
} as const;

export const PROJECT_STATUS_ID_MAP: Record<number, ProjectStatusKey> = {
  1: "paid",
  2: "paid",
  3: "assigned",
  4: "inProgress",
  5: "partiallyCompleted",
  6: "completed",
  7: "pendingReview",
  10: "draft",
} as const;

export const PROJECT_STATUS_NAMES = {
  [PROJECT_STATUS.PAID]: "Paid",
  [PROJECT_STATUS.PAID_ALT]: "Paid",
  [PROJECT_STATUS.ASSIGNED]: "Assigned",
  [PROJECT_STATUS.IN_PROGRESS]: "In Progress",
  [PROJECT_STATUS.PARTIALLY_COMPLETED]: "Partially Completed",
  [PROJECT_STATUS.COMPLETED]: "Completed",
  [PROJECT_STATUS.PENDING_REVIEW]: "Pending Customer Review",
  [PROJECT_STATUS.DRAFT]: "Draft",
} as const;

// ===== ORDER STATUSES =====

export enum ORDER_STATUS {
  DRAFT = 1,
  ACTIVE = 2,
  ASSIGNED = 3,
  IN_PROGRESS = 4,
  PARTIALLY_COMPLETED = 5,
  COMPLETED = 6,
  PENDING_REVIEW = 7,
  DRAFT_ALT = 10, // Alternative draft status
}

export interface OrderStatusInfo {
  name: string;
  bg: string;
  text: string;
}

export const ORDER_STATUS_MAP: Record<string, OrderStatusInfo> = {
  "1": { name: "Draft", bg: "#D2DAE5", text: "#0B0E10" },
  "2": { name: "Active", bg: "#86EFAC", text: "#0B0E10" },
  "3": { name: "Active", bg: "#86EFAC", text: "#0B0E10" },
  "4": { name: "Active", bg: "#86EFAC", text: "#0B0E10" },
  "5": { name: "Active", bg: "#86EFAC", text: "#0B0E10" },
  "6": { name: "Completed", bg: "#B8D0FF", text: "#0B0E10" },
  "7": { name: "Completed", bg: "#B8D0FF", text: "#0B0E10" },
  "10": { name: "Draft", bg: "#D2DAE5", text: "#0B0E10" },
} as const;

// ===== ORDER WORKFLOW STEPS =====

export enum ORDER_STEPS {
  SET_UPLOAD_DOCUMENTS_STEP,
  SET_TRANSITION_DETAILS_STEP,
  SET_PAYMENT_STEP,
  SET_THANKS_STEP,
}

export enum MOBILE_ORDER_STEPS {
  SET_UPLOAD_DOCUMENTS_STEP,
  SET_LANGUAGE_SELECTION_STEP,
  SET_TRANSLATION_TYPE_STEP,
  SET_TRANSLATION_SPEED_STEP,
  SET_TRANSLATION_PAYMENT_STEP,
  SET_THANKS_STEP,
}

// ===== PROGRESS STAGES =====

export interface ProgressStage {
  label: string;
  threshold: number;
}

export const PROGRESS_STAGES: ProgressStage[] = [
  { label: "Started", threshold: 0 },
  { label: "In progress", threshold: 50 },
  { label: "Completed", threshold: 100 },
];

export enum PROGRESS_COLOR {
  Completed = "#22C55E",
  Progress = "#2563EB",
  ProgressInactive = "#58666C",
}

// ===== UTILITY FUNCTIONS =====

/**
 * Get user role name by role type ID
 */
export const getUserRoleName = (userRoleTypeId: number): string => {
  return USER_ROLES[userRoleTypeId as UserRoleType] || "Unknown";
};

/**
 * Get project status name by status ID
 */
export const getProjectStatusName = (statusId: number): string => {
  return PROJECT_STATUS_NAMES[statusId as keyof typeof PROJECT_STATUS_NAMES] || "Unknown";
};

/**
 * Get document status name by status ID
 */
export const getDocumentStatusName = (statusId: number): string => {
  return DOCUMENT_STATUS_NAMES[statusId as DocumentStatusType] || "Unknown";
};

/**
 * Get order status info by status ID
 */
export const getOrderStatusInfo = (statusId: string | number | null | undefined): OrderStatusInfo => {
  const id = String(statusId ?? "");
  return ORDER_STATUS_MAP[id] || { name: "Active", bg: "#86EFAC", text: "#0B0E10" };
};

/**
 * Check if order status is draft
 */
export const isDraftStatus = (statusId: string | number | null | undefined): boolean => {
  const id = String(statusId ?? "");
  return id === "1" || id === "10";
};

/**
 * Get project progress by documents
 */
export const getProgressByDocuments = (documents: any[]): number => {
  if (!documents || documents.length === 0) return 0;

  const allNew = documents.every((doc: any) => Number(doc.document_status_id) === DOCUMENT_STATUS.NEW);
  const allCompleted = documents.every((doc: any) => Number(doc.document_status_id) === DOCUMENT_STATUS.COMPLETED);

  if (allCompleted) return 100;
  if (allNew) return 5;
  return 50;
};

/**
 * Calculate project status as percentage
 */
export const getProjectStatusAsPercent = (documents: any[]): number => {
  const InProgressScore = 0.2;
  const ReadyReviewScore = 0.6;
  const PaidScore = 0.1;
  const CompletedScore = 1;

  const completedDocuments = documents?.filter(
    (it: any) => Number(it.document_status_id) === DOCUMENT_STATUS.FOR_APPROVAL || 
                Number(it.document_status_id) === DOCUMENT_STATUS.COMPLETED
  ) ?? [];
  
  const inProgressDocuments = documents?.filter(
    (it: any) => Number(it.document_status_id) === DOCUMENT_STATUS.IN_PROGRESS
  ) ?? [];
  
  const readyForReviewDocuments = documents?.filter(
    (it: any) => Number(it.document_status_id) === DOCUMENT_STATUS.FOR_REVIEW
  ) ?? [];
  
  const paidDocuments = documents?.filter(
    (it: any) => Number(it.document_status_id) === DOCUMENT_STATUS.NEW
  ) ?? [];

  const totalScore =
    completedDocuments.length * CompletedScore +
    inProgressDocuments.length * InProgressScore +
    readyForReviewDocuments.length * ReadyReviewScore +
    paidDocuments.length * PaidScore;

  return documents?.length > 0 ? Math.abs((totalScore / documents.length) * 100) : 0;
};

/**
 * Get document status name for translator view
 */
export const getTranslatorDocumentStatusName = (documentStatusId: number): string => {
  if (documentStatusId >= 3) return "Completed";
  if (documentStatusId === 2) return "In Progress";
  return "New";
};
